import { CalendarClock, Eye, UserCheck, Users, UsersRound } from "lucide-react";
import { DatePicker } from "antd";
import { ContainerCard, EmployeeDetails, ModuleTitleCard } from "../../components/ui/cards";
import { Reactable } from "../../components/ui/reactable";
import { SUBORDINATES_URL } from "../../constants/api";
import { useMemo, useState } from "react";
import { Button } from "../../components/ui/button";
import { useNavigate } from "react-router-dom";
import { cn } from "../../utils/tailwindUtils";
import dayjs from "dayjs";
import { useOrgAdminBranchColumn } from "../../hooks/useOrgAdminBranchColumn";
import {
    ShiftAssignDialog,
    ShiftTableCell,
    useHierarchyShifts,
} from "../../components/common/HierarchyShift";

const SubordinatesList = () => {

    let navigate = useNavigate();
    const { showBranchColumn, insertBranchColumn, TempTransferBadge } = useOrgAdminBranchColumn("employee");

    // Admins / super-admins see the whole company by default; the toggle lets
    // them narrow to their own team (the Subordinates hierarchy configured at
    // /configuration/subordinate/). Non-admins are always team-scoped, so the
    // toggle is hidden for them.
    const isAdmin =
        JSON.parse(sessionStorage.getItem("is_admin") || "false") ||
        JSON.parse(sessionStorage.getItem("is_super_admin") || "false");

    // STATES
    const [data, setData] = useState([])
    const [totalRecords, setTotalRecords] = useState(0)
    const [scope, setScope] = useState("all"); // 'all' | 'team'

    // Only send scope=team to the backend; "all" is the default company view.
    const tableFilter = useMemo(
        () => (scope === "team" ? { scope: "team" } : {}),
        [scope]
    );

    // ---------- Shift layer (FR-100) ----------
    // Fetched separately from the table rather than added to the Subordinates
    // list endpoint: that endpoint feeds a serverSide Reactable, and the shift
    // has its own date which the table knows nothing about. Keyed by employee
    // id, so the column is a lookup.
    const [shiftDate, setShiftDate] = useState(() => dayjs());
    const [shiftTarget, setShiftTarget] = useState(null); // { employee, day }
    const {
        byEmployee: shiftByEmployee,
        shifts: shiftLegend,
        featureEnabled: shiftFeatureEnabled,
        refresh: refreshShifts,
    } = useHierarchyShifts(shiftDate);

    const shiftDateLabel = useMemo(
        () => shiftDate.format("DD MMM YYYY"),
        [shiftDate],
    );

    const shiftColumn = useMemo(() => ({
        accessor: "shift",
        Header: "Shift",
        width: "280px",
        Cell: ({ row }) => {
            const info = shiftByEmployee[row?.employee?.id];
            if (!info) return <span className="text-xs text-muted-foreground">—</span>;
            return (
                <ShiftTableCell
                    day={info.day}
                    shifts={shiftLegend}
                    canEdit={info.canEdit}
                    dateLabel={shiftDateLabel}
                    onEdit={() => setShiftTarget({ employee: row.employee, day: info.day })}
                />
            );
        },
    }), [shiftByEmployee, shiftLegend, shiftDateLabel]);

    const columns = useMemo(() => insertBranchColumn([
        {
            accessor: "employee",
            Header: "Employee",
            sortable: true,
            fixed: true,
            filterable: true,
            width: "250px",
            Cell: ({ row }) => {
                const record = row;
                let empDetails = {
                    ...record?.employee,
                    designation_id: record?.employee?.designation
                        ? {
                            id: record?.employee.designation.id,
                            designation_name: record?.employee.designation.name,
                        }
                        : { id: 0, designation_name: "" },
                    first_name: record?.employee?.employee_name || "-",
                    id: record?.employee?.employee_id || "-",
                };

                return (
                    <div className="flex items-center gap-2">
                        <EmployeeDetails details={empDetails} size="xs" />
                        {record?.on_temp_transfer && !showBranchColumn && <TempTransferBadge />}
                    </div>
                );
            },
        },
        ...(shiftFeatureEnabled ? [shiftColumn] : []),
        {
            accessor: "department",
            Header: "Department",
            filterable: true,
        },
        {
            accessor: "date_of_joining",
            Header: "Date of joining",
            filterable: true,
            filterType: "date",
            Cell: ({ value }) => {
                if (!value) return "-";
                
                try {
                    return dayjs(value, "YYYY-MM-DD").format("DD-MM-YYYY");
                } catch (error) {
                    console.error("Date formatting error:", error);
                    return value;
                }
            },
        },
        {
            accessor: "employee_group",
            Header: "Employee Group",
        },
        {
            accessor: "email_id",
            Header: "Email Id",
        },
        {
            accessor: "actions",
            Header: "Actions",
            fixed: true,
            align: "right",
            Cell: ({ row }) => {
                const handleView = () => {
                    navigate("/employee/subordinate/viewDetails", {
                        state: {
                            row: row,
                            subordinateId: row.subordinate_id,
                            employeeID: row.employee.id,
                        },
                    });
                };

                return (
                    <Button
                        variant="ghost"
                        onClick={handleView}
                        className="px-3 py-2 hover:bg-orange-50 dark:hover:bg-orange-900/20"
                        title="View Employee Details"
                    >
                        <Eye className="text-orange-500 h-4 w-4" />
                    </Button>
                );
            },
        },
    ]), [insertBranchColumn, navigate, showBranchColumn, TempTransferBadge, shiftFeatureEnabled, shiftColumn]);

    return (
        <ContainerCard>
            {/* title */}
            <ModuleTitleCard Icon={UserCheck} title={'Subordinates'} subtitle={'Subordinate details and attendance summary'} totalRecords={totalRecords} />

            {(isAdmin || shiftFeatureEnabled) && (
                <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 mb-3 px-3">
                    {isAdmin ? (
                        <div className="inline-flex items-center rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 p-1">
                            <button
                                type="button"
                                onClick={() => setScope("team")}
                                className={cn(
                                    "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors",
                                    scope === "team"
                                        ? "bg-sidebar text-white shadow-sm"
                                        : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                                )}
                            >
                                <Users className="h-3.5 w-3.5" /> My Team
                            </button>
                            <button
                                type="button"
                                onClick={() => setScope("all")}
                                className={cn(
                                    "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors",
                                    scope === "all"
                                        ? "bg-sidebar text-white shadow-sm"
                                        : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                                )}
                            >
                                <UsersRound className="h-3.5 w-3.5" /> All Users
                            </button>
                        </div>
                    ) : (
                        <span />
                    )}

                    {shiftFeatureEnabled && (
                        <div className="flex items-center gap-2">
                            <span className="flex items-center gap-1 text-xs font-medium text-gray-600 dark:text-gray-300">
                                <CalendarClock className="w-4 h-4" /> Shifts on
                            </span>
                            <DatePicker
                                format="DD-MM-YYYY"
                                value={shiftDate}
                                onChange={(value) => setShiftDate(value || dayjs())}
                                allowClear={false}
                                size="small"
                            />
                        </div>
                    )}
                </div>
            )}

            {/* table */}
            <Reactable
                columns={columns}
                data={data}
                setData={setData}
                setTotalCount={setTotalRecords}
                serverSide={true}
                url={SUBORDINATES_URL}
                filter={tableFilter}
                fillHeight
                rowSize={"sm"}
                isCSVRequired
                isPDFRequired
            />

            <ShiftAssignDialog
                open={!!shiftTarget}
                onClose={() => setShiftTarget(null)}
                employee={shiftTarget?.employee}
                date={shiftDate}
                day={shiftTarget?.day}
                shifts={shiftLegend}
                onSaved={refreshShifts}
            />
        </ContainerCard>
    );
}

export default SubordinatesList;